/**
 * PDP V2 Accessories Slider
 * Handles navigation, add-to-cart, and quantity picker functionality
 */

class PdpV2Accessories {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.pdp-v2-accessories__track');
    this.slider = container.querySelector('.pdp-v2-accessories__slider');
    this.cards = container.querySelectorAll('.pdp-v2-accessory-card');
    this.prevBtn = container.querySelector('.pdp-v2-accessories__nav-btn--prev');
    this.nextBtn = container.querySelector('.pdp-v2-accessories__nav-btn--next');
    this.dots = container.querySelectorAll('.pdp-v2-accessories__dot');

    this.currentSlide = 0;
    this.productsPerPage = parseInt(this.track?.dataset.productsPerPage) || 3;
    this.totalSlides = this.cards.length;
    this.cartItems = {};

    // Seed whitelist from rendered cards (catches cases where metafield isn't set
    // but some accessories are visible because they're not yet in the cart)
    this._seedWhitelistFromCards();

    this.init();
  }

  _seedWhitelistFromCards() {
    const existing = this.container.dataset.accessoryProductIds || '';
    const ids = new Set(existing.split(',').filter(Boolean));

    // Read IDs from the hidden span embedded in innerHTML — this survives
    // global.js replacing innerHTML after the AJAX recommendations fetch
    const idsEl = this.container.querySelector('[data-accessory-ids]');
    if (idsEl?.dataset.accessoryIds) {
      idsEl.dataset.accessoryIds.split(',').filter(Boolean).forEach((id) => ids.add(id));
    }

    // Also seed from any currently-rendered cards
    this.cards.forEach((card) => {
      if (card.dataset.productId) ids.add(String(card.dataset.productId));
    });

    // Persist discovered IDs to localStorage so they survive future loads where
    // all items are already in cart (Shopify returns 0 recommendations in that case)
    const productId = this.container.dataset.productId;
    const storageKey = productId ? `pdp-accessories-${productId}` : null;
    if (storageKey) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) stored.split(',').filter(Boolean).forEach((id) => ids.add(id));
        if (ids.size > 0) localStorage.setItem(storageKey, [...ids].join(','));
      } catch (e) {}
    }

    this.container.dataset.accessoryProductIds = [...ids].join(',');
  }

  async init() {
    if (!this.track) return;

    this.calculateDimensions();
    this.bindEvents();
    this.updateNavigation();
    this.setupResponsive();

    await this.syncWithCart();

    this.cards = this.container.querySelectorAll('.pdp-v2-accessory-card');
    if (this.cards.length > 0) {
      this.container.classList.remove('quick-add-hidden');
    } else {
      this.container.style.display = 'none';
    }
  }

  bindEvents() {
    // Navigation buttons
    this.prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.goToSlide(this.currentSlide - 1);
    });
    this.nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.goToSlide(this.currentSlide + 1);
    });
    
    // Pagination dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Bind add/quantity events for each card
    this.cards.forEach(card => {
      const actionsContainer = card.querySelector('.pdp-v2-accessory-card__actions');
      if (!actionsContainer) return;
      
      const variantId = actionsContainer.dataset.variantId;
      const addBtn = actionsContainer.querySelector('[data-add-accessory]');
      const decreaseBtn = actionsContainer.querySelector('[data-qty-decrease]');
      const increaseBtn = actionsContainer.querySelector('[data-qty-increase]');
      
      addBtn?.addEventListener('click', () => this.handleAdd(variantId, actionsContainer));
      decreaseBtn?.addEventListener('click', () => this.handleDecrease(variantId, actionsContainer));
      increaseBtn?.addEventListener('click', () => this.handleIncrease(variantId, actionsContainer));
    });
    
    // Touch/scroll handling for mobile
    this.track.addEventListener('scroll', () => this.handleScroll());
  }

  setupResponsive() {
    const resizeObserver = new ResizeObserver(() => {
      this.calculateDimensions();
      this.updateNavigation();
    });
    resizeObserver.observe(this.container);
  }

  /** Desktop: max translate so the end of the track meets the slider viewport (no clipped tail). */
  getMaxScrollOffsetDesktop() {
    if (!this.track || !this.slider) return 0;
    return Math.max(0, this.track.scrollWidth - this.slider.clientWidth);
  }

  /** Distance from one card start to the next (card width + flex gap), measured when possible. */
  getDesktopCardStep() {
    if (this.cards.length >= 2) {
      const a = this.cards[0].offsetLeft;
      const b = this.cards[1].offsetLeft;
      if (b > a) return b - a;
    }
    const w = this.cards[0]?.offsetWidth || 300;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 16;
    return w + (Number.isFinite(gap) ? gap : 16);
  }

  calculateDimensions() {
    this.totalSlides = this.cards.length;

    if (window.innerWidth >= 750) {
      this.cardWidth = this.getDesktopCardStep();
      this.visibleCards = Math.floor(this.track.clientWidth / this.cardWidth) || 1;
    } else {
      this.cardWidth = this.cards[0]?.offsetWidth || this.track.clientWidth;
      this.visibleCards = 1;
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;
    
    this.currentSlide = index;

    if (window.innerWidth >= 750) {
      const offset = Math.min(index * this.cardWidth, this.getMaxScrollOffsetDesktop());
      this.track.style.transform = `translateX(-${offset}px)`;
    } else {
      const card = this.cards[index];
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
    
    this.updateNavigation();
    this.updateDots();
  }

  handleScroll() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      const scrollLeft = this.track.scrollLeft;
      const cardWidth = this.cards[0]?.offsetWidth || 300;
      this.currentSlide = Math.round(scrollLeft / cardWidth);
      this.updateDots();
      this.updateNavigation();
    }, 100);
  }

  updateNavigation() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentSlide === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentSlide >= this.totalSlides - 1;
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('pdp-v2-accessories__dot--active', index === this.currentSlide);
    });
  }

  async syncWithCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();

      // Build whitelist of known accessory product IDs from the data attribute
      const accessoryIdsRaw = this.container.dataset.accessoryProductIds || '';
      const accessoryProductIds = new Set(accessoryIdsRaw.split(',').filter(Boolean));

      this.cartItems = {};
      const displayedVariantIds = new Set();

      this.cards.forEach((card) => {
        const actionsContainer = card.querySelector('.pdp-v2-accessory-card__actions');
        if (actionsContainer) displayedVariantIds.add(String(actionsContainer.dataset.variantId));
      });

      for (const item of cart.items) {
        const variantId = String(item.variant_id);
        this.cartItems[variantId] = { quantity: item.quantity, key: item.key, item };

        // Only inject if this product is in the accessory whitelist and not already displayed
        if (
          !displayedVariantIds.has(variantId) &&
          accessoryProductIds.has(String(item.product_id))
        ) {
          this.injectCartItemCard(item);
          displayedVariantIds.add(variantId);
        }
      }

      this.cards = this.container.querySelectorAll('.pdp-v2-accessory-card');

      this.cards.forEach((card) => {
        const actionsContainer = card.querySelector('.pdp-v2-accessory-card__actions');
        if (!actionsContainer) return;

        const variantId = String(actionsContainer.dataset.variantId);
        const cartItem = this.cartItems[variantId];

        if (cartItem && cartItem.quantity > 0) {
          this.showQuantitySelector(actionsContainer, cartItem.quantity);
        } else {
          this.showAddButton(actionsContainer);
        }
      });

      this.calculateDimensions();
      this.rebuildDots();
      this.updateNavigation();
    } catch (error) {
      console.error('[PdpV2Accessories] syncWithCart ERROR:', error);
    }
  }

  // Inject a card for a cart item that Shopify filtered out
  injectCartItemCard(item) {
    const card = document.createElement('article');
    card.className = 'pdp-v2-accessory-card pdp-v2-accessory-card--injected';
    card.dataset.productId = item.product_id;
    card.dataset.variantId = item.variant_id;
    
    // Format price
    const priceFormatted = Shopify.formatMoney ? 
      Shopify.formatMoney(item.price) : 
      '$' + (item.price / 100).toFixed(2);
    
    // Extract vendor from title or use default
    const vendor = item.vendor || 'HVAC';
    
    card.innerHTML = `
      <div class="pdp-v2-accessory-card__left">
        <a href="${item.url}" class="pdp-v2-accessory-card__image">
          ${item.image ? 
            `<img src="${item.image}" alt="${item.title}" loading="lazy" width="100" height="100">` :
            '<svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"></svg>'
          }
        </a>
        <div class="pdp-v2-accessory-card__price-badge">
          +${priceFormatted}<br>per item
        </div>
      </div>
      <div class="pdp-v2-accessory-card__right">
        <div class="pdp-v2-accessory-card__info">
          <p class="pdp-v2-accessory-card__brand">${vendor}®</p>
          <h3 class="pdp-v2-accessory-card__name">
            <a href="${item.url}">${item.title}</a>
          </h3>
        </div>
        <div class="pdp-v2-accessory-card__actions" data-variant-id="${item.variant_id}">
          <button 
            type="button" 
            class="pdp-v2-accessory-card__cta pdp-v2-accessory-card__cta--add"
            data-add-accessory
            style="display: none;"
          >
            <span class="pdp-v2-accessory-card__cta-text">Add</span>
            <span class="pdp-v2-accessory-card__cta-dot"></span>
            <span class="pdp-v2-accessory-card__cta-price">${priceFormatted}</span>
          </button>
          <div class="pdp-v2-accessory-card__qty" data-quantity-selector>
            <button type="button" class="pdp-v2-accessory-card__qty-btn" data-qty-decrease aria-label="Decrease quantity">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <span class="pdp-v2-accessory-card__qty-value" data-qty-value>${item.quantity}</span>
            <button type="button" class="pdp-v2-accessory-card__qty-btn" data-qty-increase aria-label="Increase quantity">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <p class="pdp-v2-accessory-card__added-msg" aria-live="polite">${item.quantity === 1 ? '1 Product Added' : `${item.quantity} Products Added`}</p>
        </div>
      </div>
    `;
    
    // Bind events for the injected card
    const actionsContainer = card.querySelector('.pdp-v2-accessory-card__actions');
    const variantId = String(item.variant_id);
    const addBtn = actionsContainer.querySelector('[data-add-accessory]');
    const decreaseBtn = actionsContainer.querySelector('[data-qty-decrease]');
    const increaseBtn = actionsContainer.querySelector('[data-qty-increase]');
    
    addBtn?.addEventListener('click', () => this.handleAdd(variantId, actionsContainer));
    decreaseBtn?.addEventListener('click', () => this.handleDecrease(variantId, actionsContainer));
    increaseBtn?.addEventListener('click', () => this.handleIncrease(variantId, actionsContainer));
    
    // Insert at the beginning of the track (cart items first)
    this.track.insertBefore(card, this.track.firstChild);
  }

  // Rebuild pagination dots after cards change
  rebuildDots() {
    const dotsContainer = this.container.querySelector('.pdp-v2-accessories__dots');
    if (!dotsContainer) return;
    
    // Use the same totalSlides calculation as calculateDimensions() for consistency
    // totalSlides is already set by calculateDimensions() before this is called
    const newSlideCount = this.totalSlides;
    
    // Only rebuild if count changed
    const currentDotCount = dotsContainer.querySelectorAll('.pdp-v2-accessories__dot').length;
    if (currentDotCount === newSlideCount) {
      this.dots = dotsContainer.querySelectorAll('.pdp-v2-accessories__dot');
      return;
    }
    
    // Clear and rebuild dots
    dotsContainer.innerHTML = '';
    for (let i = 0; i < newSlideCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pdp-v2-accessories__dot' + (i === this.currentSlide ? ' pdp-v2-accessories__dot--active' : '');
      dot.dataset.slideIndex = i;
      dot.setAttribute('aria-label', `Load slide ${i + 1}`);
      dot.addEventListener('click', () => this.goToSlide(i));
      dotsContainer.appendChild(dot);
    }
    
    this.dots = dotsContainer.querySelectorAll('.pdp-v2-accessories__dot');
    
    // Ensure currentSlide is within bounds after rebuild
    if (this.currentSlide >= newSlideCount) {
      this.currentSlide = Math.max(0, newSlideCount - 1);
      this.goToSlide(this.currentSlide);
    }
  }

  showAddButton(container) {
    const addBtn = container.querySelector('[data-add-accessory]');
    const qtySelector = container.querySelector('[data-quantity-selector]');
    const addedMsg = container.querySelector('.pdp-v2-accessory-card__added-msg');
    if (addBtn) {
      addBtn.style.display = '';
      addBtn.disabled = false;
      const ctaText = addBtn.querySelector('.pdp-v2-accessory-card__cta-text');
      if (ctaText) ctaText.textContent = 'Add';
      this._setAddBtnLoading(addBtn, false);
    }
    if (qtySelector) qtySelector.style.display = 'none';
    if (addedMsg) addedMsg.textContent = '';
  }

  showQuantitySelector(container, quantity) {
    const addBtn = container.querySelector('[data-add-accessory]');
    const qtySelector = container.querySelector('[data-quantity-selector]');
    const qtyValue = container.querySelector('[data-qty-value]');
    const addedMsg = container.querySelector('.pdp-v2-accessory-card__added-msg');
    if (addBtn) addBtn.style.display = 'none';
    if (qtySelector) qtySelector.style.display = '';
    if (qtyValue) qtyValue.textContent = quantity;
    if (addedMsg) addedMsg.textContent = quantity === 1 ? '1 Product Added' : `${quantity} Products Added`;
  }

  _setAddBtnLoading(addBtn, loading) {
    const ctaDot = addBtn.querySelector('.pdp-v2-accessory-card__cta-dot');
    const ctaPrice = addBtn.querySelector('.pdp-v2-accessory-card__cta-price');
    if (ctaDot) ctaDot.style.display = loading ? 'none' : '';
    if (ctaPrice) ctaPrice.style.display = loading ? 'none' : '';
  }

  async handleAdd(variantId, container) {
    const addBtn = container.querySelector('[data-add-accessory]');
    if (!addBtn || addBtn.disabled) return;
    
    // Prevent double-clicks
    if (container.dataset.loading === 'true') return;
    container.dataset.loading = 'true';
    
    const ctaText = addBtn.querySelector('.pdp-v2-accessory-card__cta-text');
    addBtn.disabled = true;
    if (ctaText) ctaText.textContent = 'Adding...';
    this._setAddBtnLoading(addBtn, true);
    
    try {
      await this.updateCartItem(variantId, 1);
      this.refreshCartUI();
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (ctaText) ctaText.textContent = 'Error';
      setTimeout(() => {
        if (ctaText) ctaText.textContent = 'Add';
        this._setAddBtnLoading(addBtn, false);
        addBtn.disabled = false;
      }, 1500);
    } finally {
      container.dataset.loading = 'false';
    }
  }

  async handleIncrease(variantId, container) {
    // Prevent double-clicks
    if (container.dataset.loading === 'true') return;
    container.dataset.loading = 'true';
    
    const currentQty = this.cartItems[variantId]?.quantity || 0;
    const newQty = currentQty + 1;
    
    try {
      await this.updateCartItem(variantId, newQty);
      // Sync with cart to get fresh state including key
      await this.syncWithCart();
      this.refreshCartUI();
    } catch (error) {
      console.error('Error increasing quantity:', error);
      // Re-sync to recover from error state
      await this.syncWithCart();
    } finally {
      container.dataset.loading = 'false';
    }
  }

  async handleDecrease(variantId, container) {
    // Prevent double-clicks
    if (container.dataset.loading === 'true') return;
    container.dataset.loading = 'true';
    
    const currentQty = this.cartItems[variantId]?.quantity || 0;
    const newQty = currentQty - 1;

    if (newQty <= 0) {
      const addBtn = container.querySelector('[data-add-accessory]');
      const qtySelector = container.querySelector('[data-quantity-selector]');
      const addedMsg = container.querySelector('.pdp-v2-accessory-card__added-msg');
      if (qtySelector) qtySelector.style.display = 'none';
      if (addedMsg) addedMsg.textContent = '';
      if (addBtn) {
        addBtn.style.display = '';
        addBtn.disabled = true;
        const ctaText = addBtn.querySelector('.pdp-v2-accessory-card__cta-text');
        if (ctaText) ctaText.textContent = 'Removing...';
        this._setAddBtnLoading(addBtn, true);
      }
    }
    
    try {
      await this.updateCartItem(variantId, newQty <= 0 ? 0 : newQty);
      // Sync with cart to get fresh state
      await this.syncWithCart();
      this.refreshCartUI();
    } catch (error) {
      console.error('Error decreasing quantity:', error);
      // Re-sync to recover from error state
      await this.syncWithCart();
    } finally {
      container.dataset.loading = 'false';
    }
  }

  async updateCartItem(variantId, quantity) {
    // Add new item
    if (!this.cartItems[variantId] && quantity > 0) {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity }],
        }),
      });
      if (!response.ok) throw new Error('Failed to add to cart');
      const data = await response.json();
      await this.syncWithCart();
      await this._fetchAndUpdateCartSections(false);
      return data;
    }

    // Update / remove existing item
    let cartItem = this.cartItems[variantId];
    if (!cartItem?.key) {
      await this.syncWithCart();
      cartItem = this.cartItems[variantId];
      if (!cartItem?.key) {
        if (quantity > 0) return this.updateCartItem(variantId, quantity);
        return { success: true };
      }
    }

    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: cartItem.key,
        quantity,
      }),
    });
    if (!response.ok) throw new Error('Failed to update cart');
    const data = await response.json();
    await this._fetchAndUpdateCartSections(false);
    return data;
  }

  async _fetchAndUpdateCartSections(openDrawer = false) {
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`
      );
      const sections = await response.json();

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.renderContents) {
        cartDrawer.renderContents({ sections }, openDrawer);

        // renderContents only removes is-empty from .drawer__inner, not cart-drawer itself.
        // Determine if the newly rendered content has items and toggle accordingly.
        const parsedSection = new DOMParser().parseFromString(
          sections['cart-drawer'] || '',
          'text/html'
        );
        const hasItems = !!parsedSection.querySelector('.cart-item');
        cartDrawer.classList.toggle('is-empty', !hasItems);
      }
    } catch (e) {}
  }

  refreshCartUI() {}
}

function initAccessories(container) {
  if (container.dataset.accessoriesInitialized) {
    return;
  }

  const track = container.querySelector('.pdp-v2-accessories__track');
  if (track) {
    container.dataset.accessoriesInitialized = 'true';
    container._pdvV2Instance = new PdpV2Accessories(container);
  }
}

// Watch for content being loaded into product-recommendations
function observeAccessories() {
  const containers = document.querySelectorAll('.pdp-v2-accessories');

  containers.forEach((container) => {
    initAccessories(container);

    const observer = new MutationObserver(() => {
      const currentTrack = container.querySelector('.pdp-v2-accessories__track');
      const instance = container._pdvV2Instance;

      // Re-init when product-recommendations replaces innerHTML (track element changes)
      if (currentTrack && (!instance || instance.track !== currentTrack)) {
        delete container.dataset.accessoriesInitialized;
        initAccessories(container);
      }
    });

    observer.observe(container, { childList: true, subtree: true });
  });
}

// Start observing when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeAccessories);
} else {
  observeAccessories();
}
